import { addDays, expandRule, isSettingLookCode, lookFamily, type Freq, type Occurrence, type PayloadOf, type RuleVersion, type SettingLookCode, type TaskTypeTag } from '@gk/contracts'
import { areaLabel, buildAreaTree, type AreaNode, type AreaRow } from './geo.ts'
import { lookForChoice, procedureOf, say, type DocumentRow, type DocumentVersionRow, type ParamDef, type TaskTypeRow, type TemplateRow, type TemplateVersionRow } from './plan.ts'

/** The set-up screens' pure parts: drafts, the checks that mirror the server's (so the user is told early; the database still decides), the payloads, and the
 *  rule of a recurrence in plain words. Nothing here reads the network or the clock. */
export type Lang = 'fr' | 'en'
export const langOf = (lang: string): Lang => (lang.startsWith('en') ? 'en' : 'fr')
const locale = (lang: string): string => (langOf(lang) === 'en' ? 'en-GB' : 'fr-FR')
export type TFn = (key: string, opts?: Record<string, unknown>) => string

/** Every reason a draft cannot be saved yet; shown as `planSetup.err.<CODE>`. */
export const SETUP_ERRORS = [
  'CODE_INVALID', 'LABEL_FR_MISSING', 'LABEL_EN_MISSING', 'TOO_MANY_SETTINGS', 'SETTING_KEY_INVALID', 'SETTING_KEY_DUPLICATE', 'SETTING_LABEL_MISSING',
  'SETTING_UNIT_TOO_LONG', 'SETTING_CHOICES_MISSING', 'SETTING_CHOICES_INVALID', 'SETTING_CHOICES_DUPLICATE', 'SETTING_LOOKS_MIXED',
  'TITLE_MISSING', 'BODY_MISSING', 'BODY_TOO_LONG',
  'TYPE_MISSING', 'DURATION_INVALID', 'CREW_INVALID', 'TOO_MANY_AREAS', 'TOO_MANY_QUALS', 'TOO_MANY_MACHINES', 'PARAM_VALUE_INVALID',
  'TEMPLATE_MISSING', 'WEEKDAYS_MISSING', 'MONTHDAYS_MISSING', 'MONTHDAYS_INVALID', 'INTERVAL_INVALID', 'TIME_INVALID', 'FROM_MISSING', 'TO_BEFORE_FROM', 'TOO_MANY_DATES', 'ROTATION_EMPTY',
] as const
export type SetupError = (typeof SETUP_ERRORS)[number]

// ---------------------------------------------------------------------------------------------------------- small helpers
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/
export const CODE_RE = /^[a-z][a-z0-9_]{1,40}$/
export const KEY_RE = /^[a-z][a-z0-9_]{0,30}$/

/** Accents and punctuation out, words joined by underscores, starting with a letter. */
export function snake(label: string, max: number): string {
  const s = label.replace(/œ/gi, 'oe').replace(/æ/gi, 'ae').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^[^a-z]+/, '')
  return s.slice(0, max).replace(/_+$/, '')
}
/** A task type code from its French label ("Tonte des greens" -> "tonte_des_greens"); empty when nothing usable is left. */
export const suggestCode = (labelFr: string): string => { const c = snake(labelFr, 41); return CODE_RE.test(c) ? c : '' }
/** A setting key from its French label. */
export const suggestKey = (labelFr: string): string => { const k = snake(labelFr, 31); return KEY_RE.test(k) ? k : '' }

/** An optional whole number between two bounds: blank is fine (null), anything else must be an integer in range. */
export function optInt(raw: string, min: number, max: number): { value: number | null; ok: boolean } {
  const s = raw.trim()
  if (!s) return { value: null, ok: true }
  const n = Number(s)
  return Number.isInteger(n) && n >= min && n <= max ? { value: n, ok: true } : { value: null, ok: false }
}
/** A setting's number as typed ("3,5" or "3.5"). */
export const parseNumber = (raw: string): number | null => { const s = raw.trim().replace(',', '.'); return /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : null }
/** Only the languages that have a text (the server refuses an empty one). */
export function bilingual(fr: string, en: string, norm: (s: string) => string = (s) => s.trim()): { fr?: string; en?: string } {
  const f = norm(fr), e = norm(en)
  return { ...(f ? { fr: f } : {}), ...(e ? { en: e } : {}) }
}
export const fmtDay = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
/** "lun. 19/10/2026" */
export const fmtDayLong = (iso: string, lang: string): string =>
  new Intl.DateTimeFormat(locale(lang), { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`))
/** "1 nov." for a 'MM-DD' (2000 is a leap year, so 02-29 exists). */
export const fmtMonthDay = (md: string, lang: string): string =>
  new Intl.DateTimeFormat(locale(lang), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, Number(md.slice(0, 2)) - 1, Number(md.slice(3, 5)))))
/** 1 = Monday .. 7 = Sunday (2024-01-01 was a Monday). */
export const weekdayName = (n: number, lang: string, style: 'long' | 'short' = 'long'): string =>
  new Intl.DateTimeFormat(locale(lang), { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, n)))
export const monthName = (m: number, lang: string): string => new Intl.DateTimeFormat(locale(lang), { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, m - 1, 1)))
export const daysInMonth = (m: number): number => new Date(Date.UTC(2000, m, 0)).getUTCDate()
const list = (items: string[], lang: string): string => new Intl.ListFormat(locale(lang), { style: 'long', type: 'conjunction' }).format(items)

/** The newest version of anything versioned (the published one is always the highest number: publishing retires the others). */
export function latestOf<T extends { version_no: number }>(versions: readonly T[]): T | undefined {
  let best: T | undefined
  for (const v of versions) if (!best || v.version_no > best.version_no) best = v
  return best
}

// ---------------------------------------------------------------------------------------------------------- task types
/** M25 (docs/M25.md E): a choice of a setting is a ROW: its word, and the picture the club chose for it (or none). The row keeps an id of its own, so that
 *  editing its word keeps its picture: the picture hangs on the row, and `looksOfDraft` writes it under whatever the word is when the draft is sent. */
export interface ChoiceRow { id: string; text: string; look: SettingLookCode | null }
let rowSeq = 0
export const newChoiceRow = (text = '', look: SettingLookCode | null = null): ChoiceRow => ({ id: `choice-${++rowSeq}`, text, look })
export interface ParamDraft { key: string; fr: string; en: string; kind: ParamDef['kind']; unit: string; choices: ChoiceRow[]; locked: boolean; keyTouched: boolean }
export const MAX_SETTINGS = 12
export const MAX_CHOICES = 20
export const blankParam = (): ParamDraft => ({ key: '', fr: '', en: '', kind: 'number', unit: '', choices: [], locked: false, keyTouched: false })
/** `looks`: the pictures stored for this setting's choices (choice text -> code); a code this build does not know is not a picture here. */
export const draftOfDef = (d: ParamDef, looks?: Readonly<Record<string, string>>): ParamDraft => ({
  key: d.key, fr: d.label.fr, en: d.label.en, kind: d.kind, unit: d.unit ?? '', locked: true, keyTouched: true,
  choices: (d.choices ?? []).map((c) => { const code = looks && Object.hasOwn(looks, c) ? looks[c] : undefined; return newChoiceRow(c, isSettingLookCode(code) ? code : null) }),
})
/** A pasted or typed list ("a, b; c") as words: split on commas, semicolons and line breaks, blanks and repeats dropped. */
export const splitChoices = (s: string): string[] => [...new Set(s.split(/[,;\n]/).map((c) => c.trim()).filter(Boolean))]
/** A list with the item at `i` moved one place up (-1) or down (+1); unchanged at the ends; never in place. */
export function moveItem<T>(list: readonly T[], i: number, by: -1 | 1): T[] {
  const j = i + by, out = [...list]
  if (i < 0 || i >= out.length || j < 0 || j >= out.length) return out
  ;[out[i], out[j]] = [out[j]!, out[i]!]
  return out
}
const wordsOf = (d: ParamDraft): string[] => d.choices.map((r) => r.text.trim()).filter(Boolean)
export function defOfDraft(d: ParamDraft): ParamDef {
  const def: ParamDef = { key: d.key.trim(), label: { fr: d.fr.trim(), en: d.en.trim() }, kind: d.kind }
  if (d.kind === 'number' && d.unit.trim()) def.unit = d.unit.trim()
  if (d.kind === 'choice') def.choices = [...new Set(wordsOf(d))]
  return def
}
/** A definition as the editor would write it back (null units dropped), so "did anything change" compares like with like. */
export const normalizeDef = (d: ParamDef): ParamDef => defOfDraft(draftOfDef(d))
/** The pictures of a draft as the server stores them: setting key -> choice word -> code, choice settings only, blank rows never (a repeated word keeps its first). */
export function looksOfDraft(params: readonly ParamDraft[]): Record<string, Record<string, SettingLookCode>> {
  const out: Record<string, Record<string, SettingLookCode>> = {}
  for (const p of params) {
    if (p.kind !== 'choice') continue
    const by: Record<string, SettingLookCode> = {}
    for (const r of p.choices) { const w = r.text.trim(); if (w && r.look && !Object.hasOwn(by, w)) by[w] = r.look }
    if (Object.keys(by).length > 0) out[p.key.trim()] = by
  }
  return out
}
/** The pictures stored on a task type, only those that still name a choice of a choice setting (the way `lookForChoice` reads them). */
function storedLooks(tt: TaskTypeRow): Record<string, Record<string, SettingLookCode>> {
  const out: Record<string, Record<string, SettingLookCode>> = {}
  for (const d of tt.param_defs) {
    if (d.kind !== 'choice') continue
    const by: Record<string, SettingLookCode> = {}
    for (const c of d.choices ?? []) { const code = lookForChoice(tt, d.key, c); if (code) by[c] = code }
    if (Object.keys(by).length > 0) out[d.key] = by
  }
  return out
}
const canon = (o: Record<string, Record<string, string>>): string => JSON.stringify(Object.keys(o).sort().map((k) => [k, Object.keys(o[k]!).sort().map((c) => [c, o[k]![c]])]))
/** The family of the pictures a setting already uses (they are all of one), null while it has none. */
export const familyOfRows = (rows: readonly ChoiceRow[]): ReturnType<typeof lookFamily> => { for (const r of rows) if (r.look) return lookFamily(r.look); return null }

export function paramDraftProblems(ds: ParamDraft[]): SetupError[] {
  const out = new Set<SetupError>()
  if (ds.length > MAX_SETTINGS) out.add('TOO_MANY_SETTINGS')
  const seen = new Set<string>()
  for (const d of ds) {
    const key = d.key.trim()
    if (!KEY_RE.test(key)) out.add('SETTING_KEY_INVALID')
    else if (seen.has(key)) out.add('SETTING_KEY_DUPLICATE')
    seen.add(key)
    if (!d.fr.trim() || !d.en.trim()) out.add('SETTING_LABEL_MISSING')
    if (d.kind === 'number' && d.unit.trim().length > 12) out.add('SETTING_UNIT_TOO_LONG')
    if (d.kind === 'choice') {
      const c = wordsOf(d)
      if (c.length === 0) out.add('SETTING_CHOICES_MISSING')
      else if (c.length > MAX_CHOICES || c.some((x) => x.length > 60)) out.add('SETTING_CHOICES_INVALID')
      else if (new Set(c).size !== c.length) out.add('SETTING_CHOICES_DUPLICATE')
      const families = new Set(d.choices.filter((r) => r.text.trim() && r.look).map((r) => lookFamily(r.look!)))
      if (families.size > 1) out.add('SETTING_LOOKS_MIXED')
    }
  }
  return [...out]
}

/** M16 (Z's opening mechanism): the words the code reads on a task type, each with the key of its label and of the one line that says what it does. The screen
 *  offers these; the server admits any word of the pattern (a later part adds its word here and in `TASK_TYPE_TAGS`). The tag names a mechanism, never the task. */
export const TASK_TAG_TEXT: Readonly<Record<TaskTypeTag, { label: string; help: string }>> = {
  pins: { label: 'planSetup.types.tagPins', help: 'planSetup.types.tagPinsHelp' },
  habitat: { label: 'planSetup.types.tagHabitat', help: 'planSetup.types.tagHabitatHelp' },
  // M25 (Z's opening; S owns this file after it): at the finish, the person confirms the settings actually used (M25-F)
  confirm_settings: { label: 'planSetup.types.tagConfirmSettings', help: 'planSetup.types.tagConfirmSettingsHelp' },
}
/** A tag's name on a screen: the known word's label, a word the club put another way as it is. */
export const tagLabel = (tag: string, t: TFn): string => (tag in TASK_TAG_TEXT ? t(TASK_TAG_TEXT[tag as TaskTypeTag].label) : tag)
const sortedTags = (tags: readonly string[]): string[] => [...new Set(tags)].sort()

export interface TaskTypeDraft { code: string; fr: string; en: string; areaTypes: string[]; params: ParamDraft[]; tags: string[] }
export const blankTaskType = (): TaskTypeDraft => ({ code: '', fr: '', en: '', areaTypes: [], params: [], tags: [] })
export const draftOfTaskType = (tt: TaskTypeRow): TaskTypeDraft =>
  ({ code: tt.code, fr: tt.label.fr, en: tt.label.en, areaTypes: [...tt.applicable_area_types], params: tt.param_defs.map((d) => draftOfDef(d, storedLooks(tt)[d.key])), tags: [...(tt.tags ?? [])] })

export function taskTypeProblems(d: TaskTypeDraft, creating: boolean): SetupError[] {
  const out: SetupError[] = []
  if (creating && !CODE_RE.test(d.code.trim())) out.push('CODE_INVALID')
  if (!d.fr.trim()) out.push('LABEL_FR_MISSING')
  if (!d.en.trim()) out.push('LABEL_EN_MISSING')
  return [...out, ...paramDraftProblems(d.params)]
}
/** A new type is defined in ONE command, `task_type.define@2` (M25-Z): the settings, the pictures of their choices and the tags together (@1 knew no tags and
 *  needed a second command after it). Nothing to say about pictures or tags, nothing sent for them. */
export function buildTaskTypeDefine(id: string, d: TaskTypeDraft): PayloadOf<'task_type.define@2'> {
  const looks = looksOfDraft(d.params), tags = sortedTags(d.tags)
  return {
    task_type_id: id, code: d.code.trim(), label: { fr: d.fr.trim(), en: d.en.trim() }, applicable_area_types: [...d.areaTypes].sort(), param_defs: d.params.map(defOfDraft),
    ...(tags.length > 0 ? { tags } : {}), ...(Object.keys(looks).length > 0 ? { choice_looks: looks } : {}),
  }
}
/** Only what changed is sent (`task_type.update@3` since M25: @2 plus the pictures): the server then refuses a stale edit only when the same thing was changed meanwhile.
 *  The pictures go (the whole object, which replaces the stored one) only when they differ from those stored: a draft whose words or order changed but whose pictures
 *  did not sends the definitions alone and the server keeps every picture that still names a choice; a RENAME changes the word under the same picture, so it is sent
 *  with the definitions, in the same update. null = nothing to save. */
export function buildTaskTypeUpdate(tt: TaskTypeRow, d: TaskTypeDraft): PayloadOf<'task_type.update@3'> | null {
  const set: PayloadOf<'task_type.update@3'>['set'] = {}
  if (d.fr.trim() !== tt.label.fr || d.en.trim() !== tt.label.en) set.label = { fr: d.fr.trim(), en: d.en.trim() }
  const kinds = [...d.areaTypes].sort()
  if (JSON.stringify(kinds) !== JSON.stringify([...tt.applicable_area_types].sort())) set.applicable_area_types = kinds
  const defs = d.params.map(defOfDraft)
  if (JSON.stringify(defs) !== JSON.stringify(tt.param_defs.map(normalizeDef))) set.param_defs = defs
  const tags = sortedTags(d.tags)
  if (JSON.stringify(tags) !== JSON.stringify(sortedTags(tt.tags ?? []))) set.tags = tags
  const looks = looksOfDraft(d.params)
  if (canon(looks) !== canon(storedLooks(tt))) set.choice_looks = looks
  return Object.keys(set).length > 0 ? { task_type_id: tt.id, expected_revision: tt.revision, set } : null
}
export const buildTaskTypeArchive = (tt: TaskTypeRow, archived: boolean): PayloadOf<'task_type.update@2'> => ({ task_type_id: tt.id, expected_revision: tt.revision, set: { archived } })
/** The settings that a published template of this task type already gives a value to: removing the definition would make the next job from it fail. */
export function usedParamKeys(code: string, templates: readonly TemplateRow[], versions: readonly TemplateVersionRow[]): Set<string> {
  const ids = new Set(templates.filter((t) => t.task_type_code === code).map((t) => t.id))
  return new Set(versions.filter((v) => ids.has(v.template_id) && v.status === 'published').flatMap((v) => Object.keys(v.params)))
}

// ---------------------------------------------------------------------------------------------------------- procedures
export const stepsOf = (text: string): string[] => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
export const normalizeSteps = (text: string): string => stepsOf(text).join('\n')
export const MAX_BODY = 20000

export interface DocDraft { titleFr: string; titleEn: string; bodyFr: string; bodyEn: string }
export const blankDoc = (): DocDraft => ({ titleFr: '', titleEn: '', bodyFr: '', bodyEn: '' })
export const draftOfDocVersion = (v: DocumentVersionRow): DocDraft => ({ titleFr: v.title.fr ?? '', titleEn: v.title.en ?? '', bodyFr: v.body?.fr ?? '', bodyEn: v.body?.en ?? '' })
export function docProblems(d: DocDraft): SetupError[] {
  const out: SetupError[] = []
  if (!d.titleFr.trim() && !d.titleEn.trim()) out.push('TITLE_MISSING')
  if (stepsOf(d.bodyFr).length === 0 && stepsOf(d.bodyEn).length === 0) out.push('BODY_MISSING')
  if (normalizeSteps(d.bodyFr).length > MAX_BODY || normalizeSteps(d.bodyEn).length > MAX_BODY) out.push('BODY_TOO_LONG')
  return out
}
/** `kind` goes only with the first version of a new document (the server refuses a different kind later). */
export const buildDocumentPublish = (ids: { documentId: string; versionId: string }, d: DocDraft, isNew: boolean): PayloadOf<'document.publish_version@1'> => ({
  document_id: ids.documentId, version_id: ids.versionId, ...(isNew ? { kind: 'procedure' as const } : {}),
  title: bilingual(d.titleFr, d.titleEn), body: bilingual(d.bodyFr, d.bodyEn, normalizeSteps),
})

export interface ProcedureRow { doc: DocumentRow; latest: DocumentVersionRow; history: DocumentVersionRow[]; templateVersions: number; outdatedUsers: number }
/** The procedures, newest wording first in each row, with how many template versions point at them (and how many still use an older wording). */
export function procedureRows(docs: readonly DocumentRow[], versions: readonly DocumentVersionRow[], tvs: readonly TemplateVersionRow[], lang: string): ProcedureRow[] {
  const rows: ProcedureRow[] = []
  for (const doc of docs) {
    if (doc.kind !== 'procedure') continue
    const history = versions.filter((v) => v.document_id === doc.id).sort((a, b) => b.version_no - a.version_no)
    const latest = history[0]
    if (!latest) continue
    const ids = new Set(history.map((v) => v.id))
    const pointing = tvs.filter((tv) => tv.procedure_document_version_id !== null && ids.has(tv.procedure_document_version_id))
    rows.push({ doc, latest, history, templateVersions: pointing.length, outdatedUsers: pointing.filter((tv) => tv.status === 'published' && tv.procedure_document_version_id !== latest.id).length })
  }
  return rows.sort((a, b) => say(a.latest.title, lang).localeCompare(say(b.latest.title, lang), lang))
}

// ---------------------------------------------------------------------------------------------------------- job templates
export const MAX_AREAS = 60
export const MAX_QUALS = 10
export const MAX_MACHINES = 10
export interface TemplateDraft {
  typeCode: string; titleFr: string; titleEn: string; instrFr: string; instrEn: string; procedureVersionId: string; duration: string; crew: string; group: string
  areaIds: string[]; qualIds: string[]; machines: string[]; params: Record<string, string>
}
export const blankTemplate = (): TemplateDraft =>
  ({ typeCode: '', titleFr: '', titleEn: '', instrFr: '', instrEn: '', procedureVersionId: '', duration: '', crew: '', group: '1', areaIds: [], qualIds: [], machines: [], params: {} })

export interface AreaChoice { id: string; code: string; label: string; context: string; typeCode: string }
/** Every unarchived area in course order, with where it sits ("Parcours Rouge › Trou 3"): a hundred greens are only told apart by their hole. */
export function areaChoices(areas: readonly AreaRow[], lang: string): AreaChoice[] {
  const byId = new Map(areas.map((a) => [a.id, a]))
  const context = (a: AreaRow): string => {
    const names: string[] = []
    for (let p = a.parent_id ? byId.get(a.parent_id) : undefined, n = 0; p && n < 12; p = p.parent_id ? byId.get(p.parent_id) : undefined, n++) {
      if (p.area_type_code !== 'site_root') names.unshift(areaLabel(p, lang))
    }
    return names.join(' › ')
  }
  const out: AreaChoice[] = []
  const walk = (n: AreaNode) => { out.push({ id: n.area.id, code: n.area.code ?? '', label: areaLabel(n.area, lang), context: context(n.area), typeCode: n.area.area_type_code }); n.children.forEach(walk) }
  buildAreaTree(areas.map((a) => ({ ...a })), [], lang).forEach(walk)
  return out
}
/** The areas a task type applies to (no kind listed = any). */
export const applicableAreas = (choices: readonly AreaChoice[], tt: Pick<TaskTypeRow, 'applicable_area_types'> | undefined): AreaChoice[] =>
  tt && tt.applicable_area_types.length > 0 ? choices.filter((c) => tt.applicable_area_types.includes(c.typeCode)) : [...choices]

/** What a published version becomes in the form. Areas that are archived or no longer of a kind the task type applies to are left out (the server would refuse
 *  them); `dropped` says how many, so the user is told. */
export function draftOfTemplateVersion(v: TemplateVersionRow, tt: TaskTypeRow | undefined, areas: readonly AreaChoice[]): { draft: TemplateDraft; dropped: number } {
  const ok = new Set(applicableAreas(areas, tt).map((a) => a.id))
  const keep = v.default_area_ids.filter((id) => ok.has(id))
  const params: Record<string, string> = {}
  for (const d of tt?.param_defs ?? []) { const x = v.params[d.key]; if (x !== undefined) params[d.key] = String(x) }
  return {
    dropped: v.default_area_ids.length - keep.length,
    draft: {
      typeCode: tt?.code ?? '', titleFr: v.title.fr ?? '', titleEn: v.title.en ?? '', instrFr: v.instructions?.fr ?? '', instrEn: v.instructions?.en ?? '',
      procedureVersionId: v.procedure_document_version_id ?? '', duration: v.default_duration_min === null ? '' : String(v.default_duration_min),
      crew: v.required_crew === null ? '' : String(v.required_crew), group: String(v.sequence_group), areaIds: keep, qualIds: [...v.required_qualification_type_ids],
      machines: [...v.default_asset_type_codes], params,
    },
  }
}

/** The typed value of every setting that has one (a blank is "not set": the job card shows nothing for it). */
export function paramValuesOf(defs: readonly ParamDef[], raw: Record<string, string>): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const d of defs) {
    const v = (raw[d.key] ?? '').trim()
    if (!v) continue
    if (d.kind === 'number') { const n = parseNumber(v); if (n !== null) out[d.key] = n } else out[d.key] = v
  }
  return out
}
const paramValueOk = (d: ParamDef, raw: string | undefined): boolean => {
  const v = (raw ?? '').trim()
  if (!v) return true
  if (d.kind === 'number') return parseNumber(v) !== null
  if (d.kind === 'choice') return (d.choices ?? []).includes(v)
  return v.length <= 200
}
export const paramProblems = (defs: readonly ParamDef[], raw: Record<string, string>): boolean => defs.some((d) => !paramValueOk(d, raw[d.key]))

export function templateProblems(d: TemplateDraft, tt: TaskTypeRow | undefined, creating: boolean): SetupError[] {
  const out: SetupError[] = []
  if (creating && !tt) out.push('TYPE_MISSING')
  if (!d.titleFr.trim() && !d.titleEn.trim()) out.push('TITLE_MISSING')
  if (!optInt(d.duration, 5, 1440).ok) out.push('DURATION_INVALID')
  if (!optInt(d.crew, 0, 50).ok) out.push('CREW_INVALID')
  if (d.areaIds.length > MAX_AREAS) out.push('TOO_MANY_AREAS')
  if (d.qualIds.length > MAX_QUALS) out.push('TOO_MANY_QUALS')
  if (d.machines.length > MAX_MACHINES) out.push('TOO_MANY_MACHINES')
  if (tt && paramProblems(tt.param_defs, d.params)) out.push('PARAM_VALUE_INVALID')
  return out
}
export function buildTemplatePublish(ids: { templateId: string; versionId: string }, d: TemplateDraft, tt: TaskTypeRow, creating: boolean): PayloadOf<'job_template.publish@1'> {
  const instructions = bilingual(d.instrFr, d.instrEn)
  return {
    template_id: ids.templateId, version_id: ids.versionId, ...(creating ? { task_type_code: tt.code } : {}),
    title: bilingual(d.titleFr, d.titleEn), ...(Object.keys(instructions).length > 0 ? { instructions } : {}),
    procedure_document_version_id: d.procedureVersionId || null, default_duration_min: optInt(d.duration, 5, 1440).value, required_crew: optInt(d.crew, 0, 50).value,
    sequence_group: Number(d.group), required_qualification_type_ids: [...d.qualIds], default_area_ids: [...d.areaIds], default_asset_type_codes: [...d.machines],
    params: paramValuesOf(tt.param_defs, d.params),
  }
}

export interface TemplateView { tpl: TemplateRow; latest: TemplateVersionRow; versions: number; taskType: TaskTypeRow | undefined; procedure: ReturnType<typeof procedureOf> }
/** One row per template: its newest version, how many there are, the task type and the procedure wording it points at (and the newest wording, if different). */
export function templateRows(tpls: readonly TemplateRow[], tvs: readonly TemplateVersionRow[], tts: readonly TaskTypeRow[], docVersions: readonly DocumentVersionRow[], lang: string): TemplateView[] {
  const rows: TemplateView[] = []
  for (const tpl of tpls) {
    const vs = tvs.filter((v) => v.template_id === tpl.id)
    const latest = latestOf(vs)
    if (!latest) continue
    rows.push({ tpl, latest, versions: vs.length, taskType: tts.find((x) => x.code === tpl.task_type_code), procedure: procedureOf(latest.procedure_document_version_id, [...docVersions]) })
  }
  return rows.sort((a, b) => say(a.latest.title, lang).localeCompare(say(b.latest.title, lang), lang))
}

// ---------------------------------------------------------------------------------------------------------- recurrences
export interface RuleShape {
  freq: Freq; interval_n: number; by_weekday: readonly number[] | null; by_monthday: readonly number[] | null; season_start_md: string | null; season_end_md: string | null
  local_start_time: string | null; valid_from: string; valid_to: string | null; exdates: readonly string[]
}
export interface RecVersion extends RuleShape {
  version_id: string; version_no: number; status: 'published' | 'retired'; template_version_id: string
  rotation: Array<Record<string, string | number>> | null; published_at: string
}
/** One row of `api.plan_recurrences`. */
export interface RecRow { recurrence_id: string; job_template_id: string; archived_at: string | null; live_jobs: number; versions: RecVersion[] | null }

export interface RuleDraft {
  freq: Freq; weekdays: number[]; monthdays: string; monthLast: boolean; interval: string; start: string
  season: boolean; seasonStart: string; seasonEnd: string; from: string; to: string; exdates: string[]
  /** null = no rotation; otherwise one entry per step, each holding the typed value of some of the task type's settings */
  rotation: Array<Record<string, string>> | null
}
export const defaultInterval = (f: Freq): string => (f === 'every_n_days' ? '2' : '1')
export const blankRule = (today: string): RuleDraft =>
  ({ freq: 'weekly', weekdays: [], monthdays: '', monthLast: false, interval: '1', start: '', season: false, seasonStart: '11-01', seasonEnd: '03-31', from: today, to: '', exdates: [], rotation: null })
export const MAX_ROTATION = 12
export const MAX_EXDATES = 400

/** "1, 15" -> [1, 15]; "last" is the toggle's job, but -1 is accepted. `bad` when something in the text is not a day of the month. */
export function parseMonthDays(text: string): { days: number[]; bad: boolean } {
  const days = new Set<number>(); let bad = false
  for (const p of text.split(/[\s,;]+/).filter(Boolean)) {
    const n = Number(p)
    if (Number.isInteger(n) && ((n >= 1 && n <= 31) || n === -1)) days.add(n); else bad = true
  }
  return { days: [...days].sort((a, b) => a - b), bad }
}
/** The days of the month as the server wants them: ascending, "last day" (-1) at the end. */
export function monthDaysOf(d: Pick<RuleDraft, 'monthdays' | 'monthLast'>): number[] {
  const days = parseMonthDays(d.monthdays).days
  return [...days.filter((x) => x > 0), ...(d.monthLast || days.includes(-1) ? [-1] : [])]
}

export function draftOfRule(v: RecVersion): RuleDraft {
  const md = v.by_monthday ?? []
  return {
    freq: v.freq, weekdays: [...(v.by_weekday ?? [])], monthdays: md.filter((x) => x > 0).join(', '), monthLast: md.includes(-1), interval: String(v.interval_n),
    start: (v.local_start_time ?? '').slice(0, 5), season: v.season_start_md !== null && v.season_end_md !== null, seasonStart: v.season_start_md ?? '11-01', seasonEnd: v.season_end_md ?? '03-31',
    from: v.valid_from, to: v.valid_to ?? '', exdates: [...v.exdates].sort(),
    rotation: v.rotation && v.rotation.length > 0 ? v.rotation.map((s) => Object.fromEntries(Object.entries(s).map(([k, x]) => [k, String(x)]))) : null,
  }
}

export function ruleProblems(d: RuleDraft, defs: readonly ParamDef[]): SetupError[] {
  const out: SetupError[] = []
  const n = Number(d.interval)
  const whole = d.interval.trim() !== '' && Number.isInteger(n)
  switch (d.freq) {
    case 'daily': break
    case 'weekly':
      if (d.weekdays.length === 0) out.push('WEEKDAYS_MISSING')
      if (!whole || n < 1 || n > 52) out.push('INTERVAL_INVALID')
      break
    case 'monthly':
      if (parseMonthDays(d.monthdays).bad) out.push('MONTHDAYS_INVALID')
      else if (monthDaysOf(d).length === 0) out.push('MONTHDAYS_MISSING')
      if (!whole || n < 1 || n > 12) out.push('INTERVAL_INVALID')
      break
    case 'every_n_days':
      if (!whole || n < 2 || n > 366) out.push('INTERVAL_INVALID')
      break
  }
  if (d.start && !TIME_RE.test(d.start)) out.push('TIME_INVALID')
  if (!DAY_RE.test(d.from)) out.push('FROM_MISSING')
  else if (d.to && (!DAY_RE.test(d.to) || d.to < d.from)) out.push('TO_BEFORE_FROM')
  if (d.exdates.length > MAX_EXDATES) out.push('TOO_MANY_DATES')
  if (d.rotation) {
    if (d.rotation.length === 0 || d.rotation.length > MAX_ROTATION) out.push('ROTATION_EMPTY')
    else if (d.rotation.some((s) => paramProblems(defs, s))) out.push('PARAM_VALUE_INVALID')
  }
  return out
}

/** The payload of `recurrence.publish`: only what the freq uses is sent (the server refuses a day list on a daily rule, an interval on "daily", ...). */
export function buildRecurrencePublish(ids: { recurrenceId: string; versionId: string; jobTemplateId?: string }, templateVersionId: string, d: RuleDraft, defs: readonly ParamDef[]): PayloadOf<'recurrence.publish@1'> {
  return {
    recurrence_id: ids.recurrenceId, version_id: ids.versionId, ...(ids.jobTemplateId ? { job_template_id: ids.jobTemplateId } : {}), template_version_id: templateVersionId, freq: d.freq,
    ...(d.freq === 'daily' ? {} : { interval_n: Number(d.interval) }),
    ...(d.freq === 'weekly' ? { by_weekday: [...new Set(d.weekdays)].sort((a, b) => a - b) } : {}),
    ...(d.freq === 'monthly' ? { by_monthday: monthDaysOf(d) } : {}),
    ...(d.season ? { season_start_md: d.seasonStart, season_end_md: d.seasonEnd } : {}),
    ...(d.start ? { local_start_time: d.start } : {}),
    valid_from: d.from, ...(d.to ? { valid_to: d.to } : {}), exdates: [...new Set(d.exdates)].sort(),
    ...(d.rotation ? { rotation: d.rotation.map((s) => paramValuesOf(defs, s)) } : {}),
  }
}
/** The same rule with another template version (the one-tap "use the latest template version"): every other field, the start date included, stays as it was. */
export function republishWithTemplateVersion(ids: { recurrenceId: string; versionId: string }, v: RecVersion, templateVersionId: string): PayloadOf<'recurrence.publish@1'> {
  return {
    recurrence_id: ids.recurrenceId, version_id: ids.versionId, template_version_id: templateVersionId, freq: v.freq,
    ...(v.freq === 'daily' ? {} : { interval_n: v.interval_n }),
    ...(v.by_weekday ? { by_weekday: [...v.by_weekday] } : {}), ...(v.by_monthday ? { by_monthday: [...v.by_monthday] } : {}),
    ...(v.season_start_md && v.season_end_md ? { season_start_md: v.season_start_md, season_end_md: v.season_end_md } : {}),
    ...(v.local_start_time ? { local_start_time: v.local_start_time.slice(0, 5) } : {}),
    valid_from: v.valid_from, ...(v.valid_to ? { valid_to: v.valid_to } : {}), exdates: [...v.exdates], ...(v.rotation ? { rotation: v.rotation.map((s) => ({ ...s })) } : {}),
  }
}

export const toRuleVersion = (v: RecVersion): RuleVersion => ({
  version_no: v.version_no, freq: v.freq, interval_n: v.interval_n, by_weekday: v.by_weekday, by_monthday: v.by_monthday, season_start_md: v.season_start_md, season_end_md: v.season_end_md,
  valid_from: v.valid_from, valid_to: v.valid_to, exdates: v.exdates, rotation: v.rotation,
})
/** The rule being typed, as the highest version of a recurrence: null while it cannot be a rule yet. */
export function draftRuleVersion(d: RuleDraft, versionNo: number, defs: readonly ParamDef[]): RuleVersion | null {
  if (ruleProblems(d, defs).length > 0) return null
  const md = monthDaysOf(d)
  return {
    version_no: versionNo, freq: d.freq, interval_n: d.freq === 'daily' ? 1 : Number(d.interval), by_weekday: d.freq === 'weekly' ? [...d.weekdays] : null, by_monthday: d.freq === 'monthly' ? md : null,
    season_start_md: d.season ? d.seasonStart : null, season_end_md: d.season ? d.seasonEnd : null, valid_from: d.from, valid_to: d.to || null, exdates: d.exdates, rotation: d.rotation,
  }
}
/** The next dates a rule makes, from `from` over the next `horizonDays`: the saved versions of the recurrence with the draft as the newest one. */
export function previewDates(saved: readonly RecVersion[], draft: RuleVersion, from: string, horizonDays = 730, limit = 10): Occurrence[] {
  return expandRule([...saved.map(toRuleVersion), draft], from, addDays(from, horizonDays)).slice(0, limit)
}
/** The newest rule, and the one that decides today (they differ while a new rule is waiting for its start date). */
export function rulesInForce(versions: readonly RecVersion[], today: string): { newest: RecVersion | undefined; deciding: RecVersion | undefined } {
  const sorted = [...versions].sort((a, b) => b.version_no - a.version_no)
  return { newest: sorted[0], deciding: sorted.find((v) => v.valid_from <= today) }
}
/** The newest published version of a template. */
export function currentTemplateVersion(templateId: string, tvs: readonly TemplateVersionRow[]): TemplateVersionRow | undefined {
  const vs = tvs.filter((v) => v.template_id === templateId)
  return latestOf(vs.filter((v) => v.status === 'published')) ?? latestOf(vs)
}

// ---------------------------------------------------------------------------------------------------------- a rule in plain words
const ordinal = (n: number, lang: string): string => {
  if (langOf(lang) === 'fr') return n === 1 ? '1er' : String(n)
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? 'th'}`
}
const hhmm = (time: string): string => time.slice(0, 5)

/** "Every Monday and Thursday at 07:00", "Every 3 days", "On the 1st and 15th, every other month", "Last day of each month". */
export function describeWhen(v: Pick<RuleShape, 'freq' | 'interval_n' | 'by_weekday' | 'by_monthday' | 'local_start_time' | 'valid_from'>, t: TFn, lang: string): string {
  const n = v.interval_n
  let when: string
  if (v.freq === 'daily') when = t('planSetup.rule.daily')
  else if (v.freq === 'every_n_days') when = t('planSetup.rule.everyNDays', { n })
  else if (v.freq === 'weekly') {
    const days = [...new Set(v.by_weekday ?? [])].sort((a, b) => a - b)
    const names = list(days.map((d) => weekdayName(d, lang)), lang)
    if (n === 1 && days.length === 7) when = t('planSetup.rule.daily')
    else if (n === 1 && days.join() === '1,2,3,4,5') when = t('planSetup.rule.weekdays')
    else when = n === 1 ? t('planSetup.rule.weeklyEach', { days: names }) : n === 2 ? t('planSetup.rule.weeklyOther', { days: names }) : t('planSetup.rule.weeklyN', { n, days: names })
  } else {
    const raw = v.by_monthday ?? [Number(v.valid_from.slice(8, 10))]
    const positive = [...new Set(raw.filter((x) => x > 0))].sort((a, b) => a - b), last = raw.includes(-1)
    const every = n === 2 ? t('planSetup.rule.everyOtherMonth') : t('planSetup.rule.everyNMonths', { n })
    if (positive.length === 0 && last) when = n === 1 ? t('planSetup.rule.lastOfEach') : t('planSetup.rule.lastOf', { every })
    else {
      const days = list([...positive.map((d) => ordinal(d, lang)), ...(last ? [t('planSetup.rule.lastDay')] : [])], lang)
      const count = positive.length + (last ? 1 : 0)
      when = n === 1 ? t('planSetup.rule.monthlyEach', { days, count }) : t('planSetup.rule.monthlyEvery', { days, every, count })
    }
  }
  return v.local_start_time ? `${when} ${t('planSetup.rule.at', { time: hhmm(v.local_start_time) })}` : when
}
/** The whole rule in one line: when, the season, the dates it is valid between, how many days are skipped. */
export function describeRule(v: RuleShape, t: TFn, lang: string): string {
  const season = v.season_start_md && v.season_end_md ? t('planSetup.rule.season', { from: fmtMonthDay(v.season_start_md, lang), to: fmtMonthDay(v.season_end_md, lang) }) : ''
  const when = describeWhen(v, t, lang) + (season ? `, ${season}` : '')
  const valid = v.valid_to ? t('planSetup.rule.validRange', { from: fmtDay(v.valid_from), to: fmtDay(v.valid_to) }) : t('planSetup.rule.validFrom', { from: fmtDay(v.valid_from) })
  const skipped = v.exdates.length > 0 ? t('planSetup.rule.skipped', { count: v.exdates.length }) : ''
  return [when, valid, skipped].filter(Boolean).join(' · ')
}
